import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Col, Divider, Form, FormInstance, Input, Radio, Row, Select, Space, ColorPicker, theme } from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import KeyFigureInput from '../../../../../components/inputs/KeyFigureInput'
import CtxFormulaSelect from '../../../../../features/formulas/components/CtxFormulaSelect'
import { currentUserSortedCompaniesSelector } from '../../../../../redux/session/currentUser/selectors'
import { RowType, ThresholdValueSetting } from '../../../../../redux/context/customReports/typesVariable'
import { ReportDataType } from '../../../../../redux/context/reports/types'
import ScenarioSelect from '../../../../../features/scenario/components/select/ScenarioSelect'
import { internalKeyFigureTypes } from '../../../keyFigures/config'
import { InfoboxComparison, InfoboxPeriod, InfoboxCompanySelection, DynamicScope } from './types'
import { resolveDisabledFlags } from './infoboxEffectiveFlags'
import { CompanyDimensionPicker } from '../../../../../shared/components/custom/CompanyDimensionPicker'
import FormulaHelp from '../../../../../features/formulas/components/FormulaHelp'

const periodValues: InfoboxPeriod[] = ['currentmonth', 'yeartodate', 'fiscalyeartodate']

const comparisonValues: InfoboxComparison[] = ['none', 'prevMonth', 'prevYear', 'budget', 'forecast']

const decimalOptions = [0, 1, 2, 3].map(d => ({ label: `${d}`, value: d }))

type DisplayPreset = 'standard' | 'compact' | 'sparkline' | 'combo'

export const presetCheckboxes: Record<DisplayPreset, Record<string, boolean>> = {
  standard: {
    showGrowth: true,
    showDiff: true,
    showCombo: false,
    showComboGrowth: false,
    showComboDiff: false,
    showSparkline: false
  },
  compact: {
    showGrowth: true,
    showDiff: false,
    showCombo: false,
    showComboGrowth: false,
    showComboDiff: false,
    showSparkline: false
  },
  sparkline: {
    showGrowth: true,
    showDiff: true,
    showCombo: false,
    showComboGrowth: false,
    showComboDiff: false,
    showSparkline: true
  },
  combo: {
    showGrowth: true,
    showDiff: true,
    showCombo: true,
    showComboGrowth: true,
    showComboDiff: true,
    showSparkline: false
  }
}

const comparisonToDataType: Record<string, ReportDataType.budget | ReportDataType.forecast> = {
  budget: ReportDataType.budget,
  forecast: ReportDataType.forecast
}

interface InfoboxSettingsProps {
  form: FormInstance
}

const infoboxRowTypes = [RowType.keyFigure, RowType.formula, RowType.source]

const InfoboxSettings: React.FC<InfoboxSettingsProps> = ({ form }) => {
  const { t } = useTranslation()
  const {
    token: { colorBgContainer }
  } = theme.useToken()
  const companies = useSelector(currentUserSortedCompaniesSelector)

  const comparison: InfoboxComparison | undefined = useWatch(['infobox', 'comparison'], form)
  const period: InfoboxPeriod | undefined = useWatch(['infobox', 'period'], form)
  const companyScope: InfoboxCompanySelection[] | undefined = useWatch(['infobox', 'companyScope'], form)
  const hasComparison = comparison !== undefined && comparison !== 'none'
  const isBudgetOrForecast = comparison === 'budget' || comparison === 'forecast'
  const isForecast = comparison === 'forecast'
  const isYtdPeriod = period === 'yeartodate' || period === 'fiscalyeartodate'
  const showCombo: boolean = useWatch(['infobox', 'showCombo'], form) ?? false
  const rowType: RowType = useWatch(['infobox', 'rowType'], form) ?? RowType.keyFigure
  const dynamicScope: DynamicScope = useWatch(['infobox', 'dynamicScope'], form) ?? 'none'
  const isDynamic = dynamicScope !== 'none'

  const disabled = useMemo(
    () => resolveDisabledFlags({ comparison, period, showCombo }),
    [comparison, period, showCombo]
  )

  const [scenarioMode, setScenarioMode] = useState<'default' | 'custom'>('default')

  useEffect(() => {
    if (isForecast) {
      setScenarioMode('custom')
      return
    }
    const hasCustomScenario = (companyScope ?? []).some(c => c.budgetingScenarioId != null)
    setScenarioMode(hasCustomScenario ? 'custom' : 'default')
  }, [companyScope, isForecast])

  const handleScenarioModeChange = (mode: 'default' | 'custom') => {
    setScenarioMode(mode)
    if (mode === 'default') {
      const updated = (companyScope ?? []).map(c => ({ ...c, budgetingScenarioId: undefined }))
      form.setFieldValue(['infobox', 'companyScope'], updated)
    }
  }

  const handleScenarioChange = (companyId: string, scenarioId: number | undefined) => {
    const updated = (companyScope ?? []).map(c => (c.id === companyId ? { ...c, budgetingScenarioId: scenarioId } : c))
    form.setFieldValue(['infobox', 'companyScope'], updated)
  }

  const companyOptions = useMemo(
    () => companies.map((c: { id: string; name: string }) => ({ id: c.id, name: c.name })),
    [companies]
  )

  const rowTypeOptions = useMemo(
    () => infoboxRowTypes.map(value => ({ label: t(`variableRowType:${value}`), value })),
    [t]
  )

  const translatedPeriodOptions = useMemo(
    () => periodValues.map(value => ({ label: t(`customReportPage:${value}`), value })),
    [t]
  )

  const translatedComparisonOptions = useMemo(
    () => comparisonValues.map(value => ({ label: t(`customReportPage:infoboxComparison_${value}`), value })),
    [t]
  )

  const thresholdOptions = useMemo(
    () =>
      Object.keys(ThresholdValueSetting).map(val => ({
        label: t(`customReportPage:${val}`),
        value: val
      })),
    [t]
  )

  const displayPresetOptions = useMemo(
    () =>
      (Object.keys(presetCheckboxes) as DisplayPreset[]).map(key => ({
        label: t(`customReportPage:infoboxPreset_${key}`),
        value: key
      })),
    [t]
  )

  const handlePresetChange = (preset: DisplayPreset) => {
    form.setFieldsValue({ infobox: presetCheckboxes[preset] })
  }

  const keyFigureId: number | undefined = useWatch(['infobox', 'keyFigureId'], form)
  const prevKeyFigureIdRef = useRef<number | undefined>(keyFigureId)

  useEffect(() => {
    if (rowType === RowType.keyFigure && keyFigureId && keyFigureId !== prevKeyFigureIdRef.current) {
      if (!form.getFieldValue('name')) {
        form.setFieldValue('name', t(`keyFigureStatement:${keyFigureId}`))
      }
    }
    prevKeyFigureIdRef.current = keyFigureId
  }, [keyFigureId, rowType, t, form])

  // Clear dependent flags when prerequisites turn off (skip initial mount)
  const prevHasComparison = useRef(hasComparison)
  const prevIsYtdPeriod = useRef(isYtdPeriod)
  const prevShowCombo = useRef(showCombo)

  useEffect(() => {
    if (prevHasComparison.current && !hasComparison) {
      form.setFieldsValue({
        infobox: { showGrowth: false, showDiff: false, showComboGrowth: false, showComboDiff: false }
      })
    }
    prevHasComparison.current = hasComparison
  }, [hasComparison, form])

  useEffect(() => {
    if (prevIsYtdPeriod.current && !isYtdPeriod) {
      form.setFieldsValue({
        infobox: { showCombo: false, showComboGrowth: false, showComboDiff: false }
      })
    }
    prevIsYtdPeriod.current = isYtdPeriod
  }, [isYtdPeriod, form])

  useEffect(() => {
    if (prevShowCombo.current && !showCombo) {
      form.setFieldsValue({
        infobox: { showComboGrowth: false, showComboDiff: false }
      })
    }
    prevShowCombo.current = showCombo
  }, [showCombo, form])

  return (
    <div style={{ marginTop: 16 }}>
      {/* Row type */}
      <Form.Item name={['infobox', 'rowType']} label={t('global:type')}>
        <Select options={rowTypeOptions} />
      </Form.Item>

      {/* Variable input — conditional on row type */}
      {rowType === RowType.keyFigure && (
        <Form.Item
          name={['infobox', 'keyFigureId']}
          label={t('customReportPage:keyFigure')}
          rules={[{ required: true, message: t('global:required-field') }]}
        >
          <KeyFigureInput />
        </Form.Item>
      )}
      {rowType === RowType.formula && (
        <Form.Item
          name={['infobox', 'formulaId']}
          label={t('global:formula')}
          rules={[{ required: true, message: t('global:required-field') }]}
        >
          <CtxFormulaSelect />
        </Form.Item>
      )}
      {rowType === RowType.source && (
        <Space.Compact block>
          <Form.Item
            style={{ width: '70%', margin: 0 }}
            name={['infobox', 'source']}
            label={t('global:formula')}
            tooltip={{ title: <FormulaHelp />, color: colorBgContainer }}
            rules={[{ required: true, message: t('global:required-field') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            style={{ width: '30%' }}
            name={['infobox', 'sourceType']}
            label={t('global:type')}
            rules={[{ required: true, message: t('global:required-field') }]}
          >
            <Select showSearch optionFilterProp="children">
              {internalKeyFigureTypes.map((keyFigureType: string) => (
                <Select.Option key={keyFigureType} value={keyFigureType}>
                  {t(`keyFigurePage:${keyFigureType}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Space.Compact>
      )}
      {/* Company & dimension picker */}
      <Form.Item
        name={['infobox', 'companyScope']}
        label={t('customReportPage:dataSources')}
        dependencies={[['infobox', 'dynamicScope']]}
        rules={[
          () => ({
            validator: (_: any, value: InfoboxCompanySelection[]) => {
              const scope = form.getFieldValue(['infobox', 'dynamicScope'])
              if (scope !== 'none') return Promise.resolve()
              if (value?.some(c => c.id)) return Promise.resolve()
              return Promise.reject(new Error(t('global:required-field')))
            }
          })
        ]}
      >
        <CompanyDimensionPicker companies={companyOptions} disabled={isDynamic} />
      </Form.Item>

      {/* Dynamic selection */}
      <Form.Item
        name={['infobox', 'dynamicScope']}
        label={t('customReportPage:dynamicScope')}
        style={{ marginBottom: 16 }}
      >
        <Radio.Group>
          <Radio value="none">{t('customReportPage:dynamicScopeNone')}</Radio>
          <Radio value="company">{t('global:company')}</Radio>
          <Radio value="companyAndDimension">{t('customReportPage:dynamicScopeCompanyAndDimension')}</Radio>
        </Radio.Group>
      </Form.Item>

      <Divider style={{ margin: '0 0 16px' }} />

      {/* Period, Comparison & Display preset */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name={['infobox', 'period']} label={t('customReportPage:period')}>
            <Select options={translatedPeriodOptions} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name={['infobox', 'comparison']} label={t('customReportPage:comparison')}>
            <Select options={translatedComparisonOptions} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('customReportPage:displayPreset')}>
            <Select options={displayPresetOptions} onChange={handlePresetChange} placeholder="—" />
          </Form.Item>
        </Col>
      </Row>

      {/* Budget scenario per company — only when scope is static; dynamic mode drops per-company scenarios */}
      {isBudgetOrForecast && dynamicScope === 'none' && (companyScope ?? []).length > 0 && (
        <>
          <Divider style={{ margin: '0 0 16px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{t('customReportPage:budgetScenario')}</span>
            {!isForecast && (
              <Radio.Group
                size="small"
                buttonStyle="solid"
                value={scenarioMode}
                onChange={e => handleScenarioModeChange(e.target.value)}
              >
                <Radio.Button value="default">{t('global:default')}</Radio.Button>
                <Radio.Button value="custom">{t('customReportPage:scenarioCustomize')}</Radio.Button>
              </Radio.Group>
            )}
          </div>
          {scenarioMode === 'custom' &&
            (companyScope ?? []).map(company => (
              <Row key={company.id} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <span style={{ fontSize: 13 }}>{company.name}</span>
                </Col>
                <Col span={16}>
                  <ScenarioSelect
                    companyId={company.id}
                    dataType={comparisonToDataType[comparison!]}
                    value={company.budgetingScenarioId ?? null}
                    onChange={val => handleScenarioChange(company.id, val ?? undefined)}
                    allowClear={!isForecast}
                    status={isForecast && !company.budgetingScenarioId ? 'error' : undefined}
                  />
                </Col>
              </Row>
            ))}
        </>
      )}

      <Divider style={{ margin: '0 0 16px' }} />

      {/* Display settings */}
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{t('customReportPage:displaySettings')}</div>

      <Row gutter={[16, 8]}>
        <Col span={12}>
          <Form.Item noStyle name={['infobox', 'showGrowth']} valuePropName="checked">
            <Checkbox disabled={disabled.showGrowth}>{t('customReportPage:showGrowth')}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item noStyle name={['infobox', 'showDiff']} valuePropName="checked">
            <Checkbox disabled={disabled.showDiff}>{t('customReportPage:showDiff')}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item noStyle name={['infobox', 'showCombo']} valuePropName="checked">
            <Checkbox disabled={disabled.showCombo}>{t('customReportPage:showCombo')}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item noStyle name={['infobox', 'showComboGrowth']} valuePropName="checked">
            <Checkbox disabled={disabled.showComboGrowth}>{t('customReportPage:showComboGrowth')}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item noStyle name={['infobox', 'showComboDiff']} valuePropName="checked">
            <Checkbox disabled={disabled.showComboDiff}>{t('customReportPage:showComboDiff')}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item noStyle name={['infobox', 'showSparkline']} valuePropName="checked">
            <Checkbox>{t('customReportPage:showSparkline')}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item noStyle name={['infobox', 'showUnit']} valuePropName="checked">
            <Checkbox>{t('customReportPage:showUnit')}</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Divider style={{ marginBlock: 16 }} />

      {/* Decimals, Threshold & Color */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={8}>
          <Form.Item name={['infobox', 'decimals']} label={t('customReportPage:decimals')}>
            <Select options={decimalOptions} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name={['infobox', 'thresholdValueSetting']} label={t('customReportPage:thresholdValueSetting')}>
            <Select options={thresholdOptions} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name={['infobox', 'color']}
            label={t('customReportPage:sparklineColor')}
            getValueFromEvent={color => color?.toHexString()}
          >
            <ColorPicker showText />
          </Form.Item>
        </Col>
      </Row>
    </div>
  )
}

export default InfoboxSettings
